import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  DialogContent,
  Typography,
  List,
  ListItem,
  Button,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  TextField,
  Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import DialogTransition from 'src/components/DialogTransition';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import Dialog from 'src/components/Dialog/Dialog';
import Preloader from 'src/components/Preloader';
import {
  LOAD_INSURANCE_EQUIPMENTS,
  loadInsuranceEquipments,
  removeEquipmentFromInsuranceAction,
  REMOVE_EQUIPMENT_FROM_INSURANCE
} from 'src/store/actions/insuranceActions';
import { selectInsuranceEquipments } from 'src/store/selectors/insuranceSelector';
import { getEquipmentDisplayName } from 'src/utils/constants/insurance';

const RemoveEquipmentAssociationDialog = ({ open, onClose, policyId }) => {
  const dispatch = useDispatch();
  const availableEquipments = useSelector(selectInsuranceEquipments);
  const loading = useSelector(
    isLoadingSelector([LOAD_INSURANCE_EQUIPMENTS.action])
  );
  const isRemoving = useSelector(
    isLoadingSelector([REMOVE_EQUIPMENT_FROM_INSURANCE.action])
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (open && policyId) {
      dispatch(loadInsuranceEquipments({ policyId }));
    }
  }, [open, dispatch, policyId]);

  const handleRemoveSelectedEquipments = values => {
    dispatch(
      removeEquipmentFromInsuranceAction(
        policyId,
        values.selectedEquipments.map(equipment => equipment.id),
        onClose
      )
    );
  };

  if (!open) return null;

  const initialValues = {
    selectedEquipments: []
  };
  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: 'md'
        }
      }}
      open={open}
      handleClose={onClose}
      TransitionComponent={DialogTransition}
      title={t('INSURANCE_VIEW.REMOVE_EQUIPMENTS')}
      maxWidth="md"
      fullWidth
    >
      <DialogContent sx={{ position: 'relative' }}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={handleRemoveSelectedEquipments}
        >
          {({ handleSubmit, values, setFieldValue }) => {
            const handleAddEquipment = (event, newValue) => {
              if (
                newValue &&
                !values.selectedEquipments.some(
                  equipment => equipment.id === newValue.id
                )
              ) {
                const newSelectedEquipments = [
                  ...values.selectedEquipments,
                  newValue
                ];
                setFieldValue('selectedEquipments', newSelectedEquipments);
              }
            };

            const handleRemoveEquipment = equipmentId => {
              const newSelectedEquipments = values.selectedEquipments.filter(
                equipment => equipment.id !== equipmentId
              );
              setFieldValue('selectedEquipments', newSelectedEquipments);
            };

            const isEquipmentSelected = equipmentId => {
              return values.selectedEquipments.some(
                equipment => equipment.id === equipmentId
              );
            };

            const availableEquipmentsFiltered =
              availableEquipments?.filter(
                equipment => !isEquipmentSelected(equipment.id)
              ) || [];

            return (
              <Form onSubmit={handleSubmit}>
                <Box mb={3} sx={{ width: '100%' }}>
                  <Typography variant="h6" marginBottom={2} gutterBottom>
                    {t('INSURANCE_VIEW.SELECT_EQUIPMENTS_TO_REMOVE')}
                  </Typography>

                  {loading && (
                    <Box display="flex" justifyContent="center" py={3}>
                      <Preloader size={30} />
                    </Box>
                  )}

                  {!loading && (
                    <Autocomplete
                      options={availableEquipmentsFiltered}
                      getOptionLabel={getEquipmentDisplayName}
                      onChange={handleAddEquipment}
                      loading={loading}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={t(
                            'INSURANCE_VIEW.SEARCH_ASSOCIATED_EQUIPMENTS'
                          )}
                          variant="outlined"
                          fullWidth
                          placeholder={t(
                            'INSURANCE_VIEW.TYPE_TO_SEARCH_EQUIPMENTS'
                          )}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Typography variant="body1">
                            {getEquipmentDisplayName(option)}
                          </Typography>
                        </Box>
                      )}
                      noOptionsText={t('INSURANCE_VIEW.NO_EQUIPMENTS_FOUND')}
                      loadingText={t('COMMON.LOADING')}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box mb={3}>
                  <Typography variant="h6" gutterBottom marginBottom={2}>
                    {t('INSURANCE_VIEW.SELECTED_EQUIPMENTS_TO_REMOVE').replace(
                      '{number}',
                      values.selectedEquipments.length
                    )}
                  </Typography>
                  {values.selectedEquipments.length > 0 ? (
                    <List dense>
                      {values.selectedEquipments.map(equipment => (
                        <ListItem key={equipment.id}>
                          <ListItemText
                            primary={getEquipmentDisplayName(equipment)}
                            secondary={`${t('COMMON.ID')}: ${equipment.id}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleRemoveEquipment(equipment.id)
                              }
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {t('INSURANCE_VIEW.NO_EQUIPMENTS_SELECTED_FOR_REMOVAL')}
                    </Typography>
                  )}
                </Box>

                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{ width: '100%' }}
                    disabled={isRemoving}
                  >
                    {t('COMMON.CANCEL')}
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ width: '100%' }}
                    color="primary"
                    type="submit"
                    disabled={
                      isRemoving || values.selectedEquipments.length === 0
                    }
                  >
                    {t('INSURANCE_VIEW.DESASIGNAR')}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>

        {isRemoving && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <Preloader size={40} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

RemoveEquipmentAssociationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  policyId: PropTypes.string.isRequired
};

export default RemoveEquipmentAssociationDialog;
